import { z } from "zod";
import {
  SharedApiError,
  normalizeApiBaseUrl,
  parseErrorResponse,
  type FetchLike,
} from "./shared";
import { mcpAppStatusSchema, mcpTrustTierSchema } from "./mcp-catalog";

// Shell Library v1 — find, install, and trust CLIs, MCPs, and future
// Shell extensions. The plan lives at
// ~/.claude/plans/there-s-currently-a-menu-eager-pnueli.md.

export const shellExtensionKindSchema = z.enum([
  "mcp",
  "cli",
  "skill",
  "recipe",
  "agent",
]);
export type ShellExtensionKind = z.infer<typeof shellExtensionKindSchema>;

export const installChannelSchema = z.enum([
  "npm",
  "pnpm",
  "brew",
  "claude",
  "curl",
  "hosted",
]);
export type InstallChannel = z.infer<typeof installChannelSchema>;

export const installCommandSchema = z.object({
  channel: installChannelSchema,
  command: z.string().min(1),
  signature: z.string().min(1),
  signed_at: z.string(),
  key_id: z.string(),
});
export type InstallCommand = z.infer<typeof installCommandSchema>;

export const maintenanceStatusSchema = z.enum([
  "active",
  "stale",
  "archived",
  "unknown",
]);

export const trustSignalsSchema = z
  .object({
    publisher_verified: z.boolean().optional(),
    publisher_verification_source: z.string().nullable().optional(),
    weekly_downloads: z.number().nullable().optional(),
    last_published_at: z.string().nullable().optional(),
    last_commit_at: z.string().nullable().optional(),
    maintenance_status: maintenanceStatusSchema.optional(),
    vuln_advisories: z.number().nullable().optional(),
  })
  .default({});
export type TrustSignals = z.infer<typeof trustSignalsSchema>;

export const shellExtensionSchema = z.object({
  id: z.string().min(1),
  slug: z.string().min(1),
  name: z.string().min(1),
  kind: shellExtensionKindSchema,
  publisher: z.string().min(1),
  description: z.string().nullable().optional(),
  icon_url: z.string().nullable().optional(),
  homepage_url: z.string().nullable().optional(),
  trust_tier: mcpTrustTierSchema,
  status: mcpAppStatusSchema,
  latest_version: z.string().nullable().optional(),
  install_commands: z.array(installCommandSchema),
  trust_signals: trustSignalsSchema,
  permissions_json: z.unknown().optional(),
  source_labels: z.array(z.string()).default([]),
  synced_at: z.string().nullable().optional(),
});
export type ShellExtension = z.infer<typeof shellExtensionSchema>;

// Sync endpoint ---------------------------------------------------------------

export const librarySyncSuccessSchema = z.object({
  ok: z.literal(true),
  counts: z.object({
    mcps: z.object({
      added: z.number(),
      updated: z.number(),
      skipped: z.number(),
    }),
    clis: z.object({
      added: z.number(),
      updated: z.number(),
      skipped: z.number(),
    }),
  }),
  synced_at: z.string(),
});
export type LibrarySyncSuccess = z.infer<typeof librarySyncSuccessSchema>;

export function librarySyncPath(): string {
  return "/api/library/sync";
}

export async function librarySyncRequest({
  apiBaseUrl,
  accessToken,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  fetchImpl?: FetchLike;
}): Promise<LibrarySyncSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${librarySyncPath()}`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  if (!response.ok) {
    throw await parseErrorResponse(response, "Library sync failed");
  }
  const payload = await response.json().catch(() => null);
  const parsed = librarySyncSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid library sync response", 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}

// Search endpoint -------------------------------------------------------------

export const librarySearchRequestSchema = z.object({
  q: z.string().default(""),
  kind: shellExtensionKindSchema.optional(),
  trust_tier: mcpTrustTierSchema.optional(),
  limit: z.number().int().positive().max(100).default(40),
});
export type LibrarySearchRequest = z.infer<typeof librarySearchRequestSchema>;

export const librarySearchSuccessSchema = z.object({
  results: z.array(shellExtensionSchema),
  synced_at: z.string().nullable(),
  sources: z.object({
    catalog: z.number(),
    mcp_registry: z.number(),
    npm: z.number(),
    claude_marketplace: z.number(),
  }),
});
export type LibrarySearchSuccess = z.infer<typeof librarySearchSuccessSchema>;

export function librarySearchPath(): string {
  return "/api/library/search";
}

export async function librarySearchRequestFn({
  apiBaseUrl,
  accessToken,
  q,
  kind,
  trustTier,
  limit,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  q?: string;
  kind?: ShellExtensionKind;
  trustTier?: z.infer<typeof mcpTrustTierSchema>;
  limit?: number;
  fetchImpl?: FetchLike;
}): Promise<LibrarySearchSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const url = new URL(
    `${normalizeApiBaseUrl(apiBaseUrl)}${librarySearchPath()}`,
  );
  if (q) url.searchParams.set("q", q);
  if (kind) url.searchParams.set("kind", kind);
  if (trustTier) url.searchParams.set("trust_tier", trustTier);
  if (typeof limit === "number") {
    url.searchParams.set("limit", String(limit));
  }
  const response = await fetchFn(url.toString(), {
    method: "GET",
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!response.ok) {
    throw await parseErrorResponse(response, "Library search failed");
  }
  const payload = await response.json().catch(() => null);
  const parsed = librarySearchSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid library search response", 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}

// List curated extensions -----------------------------------------------------

export const listLibrarySuccessSchema = z.object({
  extensions: z.array(shellExtensionSchema),
  synced_at: z.string().nullable(),
  // Populated when the last auto-bootstrap attempt failed. The client shows
  // a retry CTA instead of a "loading" spinner when this is set.
  bootstrap_error: z.string().nullable().optional(),
});
export type ListLibrarySuccess = z.infer<typeof listLibrarySuccessSchema>;

export function listLibraryPath(): string {
  return "/api/library";
}

export async function listLibraryRequest({
  apiBaseUrl,
  accessToken,
  kind,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  kind?: ShellExtensionKind;
  fetchImpl?: FetchLike;
}): Promise<ListLibrarySuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const url = new URL(`${normalizeApiBaseUrl(apiBaseUrl)}${listLibraryPath()}`);
  if (kind) url.searchParams.set("kind", kind);
  const response = await fetchFn(url.toString(), {
    method: "GET",
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to load library");
  }
  const payload = await response.json().catch(() => null);
  const parsed = listLibrarySuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid library list response", 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}

// Install lifecycle -----------------------------------------------------------

export const installSourceSchema = z.enum([
  "catalog",
  "live_search",
  "ambient",
]);
export type InstallSource = z.infer<typeof installSourceSchema>;

export const installRecordStatusSchema = z.enum([
  "pending",
  "running",
  "succeeded",
  "failed",
  "cancelled",
]);
export type InstallRecordStatus = z.infer<typeof installRecordStatusSchema>;

export const installRecordSchema = z.object({
  id: z.string().min(1),
  // Nullable: live-search installs (npm/MCP-Registry entries not yet synced
  // into the catalog) carry no `mcp_apps.id` to join against.
  extension_id: z.string().nullable(),
  slug: z.string().min(1),
  kind: shellExtensionKindSchema,
  channel: installChannelSchema,
  source: installSourceSchema,
  status: installRecordStatusSchema,
  exit_code: z.number().int().nullable().optional(),
  stderr_tail: z.string().nullable().optional(),
  installed_at: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type InstallRecord = z.infer<typeof installRecordSchema>;

export const beginInstallRequestSchema = z.object({
  extension_id: z.string().min(1),
  slug: z.string().min(1),
  kind: shellExtensionKindSchema,
  channel: installChannelSchema,
  command: z.string().min(1),
  // Signature material lives in the body (not headers) so the contract stays
  // single-serialization — both the desktop client and future ambient
  // callers can reuse the same helper.
  signature: z.string().min(1),
  signed_at: z.string().min(1),
  key_id: z.string().min(1),
  source: installSourceSchema,
  workspace_id: z.string().min(1).optional(),
});
export type BeginInstallRequest = z.infer<typeof beginInstallRequestSchema>;

export const beginInstallSuccessSchema = z.object({
  install: installRecordSchema,
});
export type BeginInstallSuccess = z.infer<typeof beginInstallSuccessSchema>;

export function beginInstallPath(): string {
  return "/api/library/installs";
}

export async function beginInstallRequestFn({
  apiBaseUrl,
  accessToken,
  body,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  body: BeginInstallRequest;
  fetchImpl?: FetchLike;
}): Promise<BeginInstallSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${beginInstallPath()}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    },
  );
  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to begin install");
  }
  const payload = await response.json().catch(() => null);
  const parsed = beginInstallSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid begin-install response", 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}

export const completeInstallRequestSchema = z.object({
  status: installRecordStatusSchema,
  exit_code: z.number().int().nullable().optional(),
  stderr_tail: z.string().nullable().optional(),
});
export type CompleteInstallRequest = z.infer<
  typeof completeInstallRequestSchema
>;

export const completeInstallSuccessSchema = z.object({
  install: installRecordSchema,
});
export type CompleteInstallSuccess = z.infer<
  typeof completeInstallSuccessSchema
>;

export function completeInstallPath(installId: string): string {
  return `/api/library/installs/${encodeURIComponent(installId)}/complete`;
}

export async function completeInstallRequestFn({
  apiBaseUrl,
  accessToken,
  installId,
  body,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  installId: string;
  body: CompleteInstallRequest;
  fetchImpl?: FetchLike;
}): Promise<CompleteInstallSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${completeInstallPath(installId)}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    },
  );
  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to complete install");
  }
  const payload = await response.json().catch(() => null);
  const parsed = completeInstallSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid complete-install response", 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}
